import { BsEye } from 'react-icons/bs';
import { FiEdit, FiExternalLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ACTIONED_BY_OPTIONS, STATE_OPTIONS } from '@/config';
import { ROUTES } from '@/config/routes';
import { AsyncNormalSelect, Button, Select } from '@/core/ui';
import type { RecordReview } from '@/graphql/gql/graphql';
import { useContentReview } from '@/store';
import { useGlobal } from '@/store/global';
import { getDbTypeHashFragment } from '@/utils';
import { formatDateFromUnix } from '@/utils/date';
import { ReasonCell } from '../../components/reason-cell';
import { ReviewActions } from './review-actions';

type CellRendererProps = {
  review: RecordReview;
  columnKey: string;
  serialNo: number;
  onStateChange: (reviewId: string, val: number | null) => Promise<void>;
  onActionedByChange: (reviewId: string, val: number | null) => Promise<void>;
  onMembersChange: (reviewId: string, keys: any) => Promise<void>;
  onDelete: (reviewId: string | number) => Promise<void>;
  updateLoading: boolean;
  deleteLoading: boolean;
};

type FeedbackCellProps = {
  review: RecordReview;
  cellValue: string;
};

const FeedbackCell: React.FC<FeedbackCellProps> = ({ review, cellValue }) => {
  const openFeedbackModal = useContentReview.use.openFeedbackModal();

  return (
    <div className='relative bg-content1 rounded-lg p-2 border border-default-200'>
      <pre className='text-sm wrap-break-word line-clamp-6 font-mono text-default-800 whitespace-pre-wrap'>
        {cellValue || '---'}
      </pre>
      <div className='absolute top-2 right-2 flex items-center gap-1'>
        <Button
          isIconOnly
          size='sm'
          variant='faded'
          color='secondary'
          className='min-w-6 w-6 h-6 '
          onPress={() => {
            openFeedbackModal(review.id?.toString() ?? '', cellValue ?? '', 'reason');
          }}
        >
          <BsEye className='size-3.5 text-default-500' />
        </Button>
        <Button
          isIconOnly
          size='sm'
          variant='faded'
          color='warning'
          className='min-w-6 w-6 h-6'
          onPress={() => {
            openFeedbackModal(review.id?.toString() ?? '', cellValue ?? '', 'comment');
          }}
        >
          <FiEdit className='size-3.5 text-default-500' />
        </Button>
      </div>
    </div>
  );
};

export const ReviewTableCell: React.FC<CellRendererProps> = ({
  review,
  columnKey,
  serialNo,
  onStateChange,
  onActionedByChange,
  onMembersChange,
  onDelete,
  deleteLoading,
}) => {
  const dbType = useGlobal.use.dbType();

  switch (columnKey) {
    case 'srNo':
      return <span className='text-sm font-medium text-default-600'>{serialNo}</span>;

    case 'id':
      return <span className='text-sm font-mono text-default-600'>{review.id}</span>;

    case 'commentId':
      return <span className='text-sm font-mono text-default-600'>{review.commentId}</span>;

    case 'comment':
      return <FeedbackCell review={review} cellValue={review.comment ?? ''} />;

    case 'reason':
      return <ReasonCell reviewId={review.id?.toString() ?? ''} reasonText={review.reason ?? ''} />;

    case 'user':
      return (
        <div className='flex flex-col'>
          {review.createdBy || '-'}
          {/* <span className='text-sm font-medium text-default-700'>
            {review.createdByUser?.displayName || review.createdByUser?.username || 'Unknown'}
          </span>
          {review.createdByUser?.username &&
            review.createdByUser.username !== review.createdByUser?.displayName && (
              <span className='text-xs text-default-500'>@{review.createdByUser.username}</span>
            )} */}
        </div>
      );

    case 'recordId':
      return review.recordId ? (
        <Link
          target='_blank'
          rel='noopener noreferrer'
          to={
            ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(review.recordId)) +
            getDbTypeHashFragment('global-state', { dbType })
          }
          className='flex items-center gap-2 text-sm text-default-600 hover:text-default-800 hover:underline'
        >
          <span className='font-mono'>{review.recordId}</span>
          <FiExternalLink className='size-3.5' />
        </Link>
      ) : (
        <span className='text-sm text-default-400'>-</span>
      );

    case 'member': {
      const members = review.members ?? [];
      const currentIds = members.map((m) => m?.userId).filter((id): id is number => id != null);
      const names = members.map((m) => m?.user?.firstName).filter((name): name is string => name != null);

      return (
        <div className='flex flex-col gap-1'>
          <AsyncNormalSelect
            size='sm'
            variant='bordered'
            description={names.join(', ')}
            name='members'
            groupBy='options'
            addValuePrefix={false}
            queryKey='admins'
            selectionMode='multiple'
            selectedKeys={new Set(currentIds.map((id) => String(id)))}
            onSelectionChange={(keys) => onMembersChange(review.id?.toString() ?? '', keys)}
            className='w-32 text-xs'
            popoverProps={{ classNames: { content: ' !p-1.5 !w-64 text-xs' } }}
            classNames={{ listbox: 'text-xs' }}
          />
        </div>
      );
    }

    case 'state':
      return (
        <div className='w-full'>
          <Select
            size='sm'
            variant='bordered'
            selectionMode='single'
            selectedKeys={review.state != null ? new Set([String(review.state)]) : new Set()}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys as Set<string>);
              const raw = arr[0];
              const next = raw ? Number.parseInt(raw, 10) : NaN;
              if (Number.isFinite(next as number)) {
                onStateChange(review.id?.toString() ?? '', next as number);
              }
            }}
            options={STATE_OPTIONS}
          />
        </div>
      );

    case 'actionedBy':
      return (
        <div className='w-full'>
          <Select
            size='sm'
            variant='bordered'
            selectionMode='single'
            selectedKeys={review.actionedBy != null ? new Set([String(review.actionedBy)]) : new Set()}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys as Set<string>);
              const raw = arr[0];
              const next = raw ? Number.parseInt(raw, 10) : NaN;
              if (Number.isFinite(next as number)) {
                onActionedByChange(review.id?.toString() ?? '', next as number);
              }
            }}
            options={ACTIONED_BY_OPTIONS}
          />
        </div>
      );

    case 'createdAt':
      return review.createdAt ? (
        <span className='text-sm text-default-600'>
          {formatDateFromUnix(review.createdAt, 'DD MMM YY | HH:mm')}
        </span>
      ) : (
        <span className='text-sm text-default-400'>-</span>
      );

    case 'actions':
      return (
        <div className='flex items-center gap-2 justify-center'>
          <ReviewActions
            review={review}
            isLoading={deleteLoading}
            onAction={async (reviewId) => {
              if (reviewId) {
                await onDelete(reviewId);
              }
            }}
          />
        </div>
      );

    default:
      return <span className='text-sm text-default-400'>-</span>;
  }
};
